import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import pluralize from 'pluralize';
import { Alert } from '../../common/entities';
import { UnifiedAPI } from '../../api/unified-api';
import { logger } from '../../utils/logger';
import { useOrg } from './OrgProvider';

// Parsed alert with keyword arrays
interface ParsedAlert {
  alert_id: string;
  keyword: string;
  channel_id: string;
  words: string[]; // OR keywords
  ands: string[]; // AND keywords (required)
  nos: string[]; // NOT keywords (excluded)
}

interface AlertContextValue {
  /** Check if message text matches any alerts, returns matched keywords */
  checkMessageForAlerts: (text: string, channelId?: string) => string[];
  /** Number of loaded alerts */
  alertCount: number;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: React.ReactNode;
}

/**
 * AlertProvider - Provides alert keyword matching for inline highlighting
 *
 * This provider loads alerts for the current organization and provides
 * a function to check if message text matches any alert keywords.
 *
 * Note: Toast notifications for alerts are NOT handled here - the backend
 * sends real notifications (SMS, email, etc.). This provider is only for
 * UI highlighting when viewing messages.
 */
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  // V2 org state (from OrgProvider)
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.org_id;

  // Local state
  const [parsedAlerts, setParsedAlerts] = useState<ParsedAlert[]>([]);

  // Load and parse alerts when org changes
  useEffect(() => {
    if (!orgId) return;

    const loadAlerts = async () => {
      try {
        const alertsData = await UnifiedAPI.getAlerts(orgId);
        if (alertsData) {
          const alerts = parseAlerts(alertsData);
          setParsedAlerts(alerts);
          logger.info('[AlertProvider] Loaded', alerts.length, 'alerts for inline matching');
        }
      } catch (error) {
        logger.error('[AlertProvider] Failed to load alerts:', error);
      }
    };

    loadAlerts();
  }, [orgId]);

  /**
   * Parse raw alerts into keyword arrays
   * Supports: OR keywords, +AND keywords, -NOT keywords
   */
  const parseAlerts = (alertsData: Record<string, Alert>): ParsedAlert[] => {
    return Object.keys(alertsData)
      .map((key) => {
        const alert = alertsData[key];
        if (alert.status === 0 || alert.keyword === '*') return null;

        const keyword = alert.keyword?.toLowerCase() || '';
        const keywords = keyword.match(/([+-]?".*?"|[+-]?[\u00BF-\u1FFF\u2C00-\uD7FF\w]+)/g);

        const words: string[] = [];
        const ands: string[] = [];
        const nos: string[] = [];

        if (keywords) {
          for (const word of keywords) {
            if (word.startsWith('+')) {
              // AND keyword (required)
              if (word[1] === '"') {
                ands.push(word.substring(2, word.length - 1));
              } else {
                ands.push(word.substring(1));
              }
            } else if (word.startsWith('-')) {
              // NOT keyword (excluded)
              if (word[1] === '"') {
                nos.push(word.substring(2, word.length - 1));
              } else {
                nos.push(word.substring(1));
              }
            } else {
              // OR keyword
              if (word[0] === '"') {
                words.push(word.substring(1, word.length - 1));
              } else {
                words.push(word);
              }
            }
          }
        }

        return {
          alert_id: key,
          keyword: alert.keyword,
          channel_id: alert.channel_id || '',
          words: words.filter((s) => s.trim()),
          ands: ands.filter((s) => s.trim()),
          nos: nos.filter((s) => s.trim())
        };
      })
      .filter((a): a is ParsedAlert => a !== null);
  };

  /**
   * Get alternate word form (plural/singular)
   */
  const getAltWord = (word: string): string => {
    const plural = pluralize(word);
    return plural === word ? pluralize.singular(word) : plural;
  };

  /**
   * Check if word exists in text (word boundary match)
   */
  const inText = (text: string, word: string): boolean => {
    try {
      return new RegExp('\\b' + word + '\\b', 'i').test(text);
    } catch (error) {
      return false;
    }
  };

  /**
   * Check message text against all alerts
   * Returns array of matched keywords for highlighting
   *
   * Logic matches backend (post-process.js):
   * - NOT keywords: if any match, skip alert
   * - AND keywords: all must match for alert to trigger
   * - OR keywords: at least one must match (only if no ANDs)
   */
  const checkMessageForAlerts = useCallback(
    (text: string, channelId?: string): string[] => {
      const matchedKeywords: string[] = [];

      for (const alert of parsedAlerts) {
        // Skip if alert is for a specific channel that doesn't match
        if (
          alert.channel_id &&
          alert.channel_id !== 'All Channels' &&
          channelId &&
          alert.channel_id !== channelId
        ) {
          continue;
        }

        let flag = 0;

        // Check NOT keywords - if any match, skip this alert
        if (alert.nos.length > 0) {
          for (const noWord of alert.nos) {
            if (inText(text, noWord) || inText(text, getAltWord(noWord))) {
              flag = 1;
              break;
            }
          }
        }
        if (flag) continue;

        // Check AND keywords - all must match
        if (alert.ands.length > 0) {
          for (const andWord of alert.ands) {
            if (!inText(text, andWord) && !inText(text, getAltWord(andWord))) {
              flag = 1;
              break;
            }
          }
        }
        if (flag) continue;

        // If we get here, ANDs passed and NOs passed
        // Backend logic: ANDs are the gate, OR words are bonus matches
        const alertMatches: string[] = [];

        if (alert.ands.length > 0) {
          // ANDs matched - alert triggers! Add AND keywords
          alertMatches.push(...alert.ands);
          // Also add any matching OR words as bonus
          for (const word of alert.words) {
            if (inText(text, word) || inText(text, getAltWord(word))) {
              alertMatches.push(word);
            }
          }
        } else if (alert.words.length > 0) {
          // No ANDs - need at least one OR word to match
          for (const word of alert.words) {
            if (inText(text, word) || inText(text, getAltWord(word))) {
              alertMatches.push(word);
            }
          }
          // Skip if no OR words matched
          if (alertMatches.length === 0) continue;
        }

        // Add this alert's matches to the global list
        matchedKeywords.push(...alertMatches);
      }

      return [...new Set(matchedKeywords)];
    },
    [parsedAlerts]
  );

  return (
    <AlertContext.Provider
      value={{
        checkMessageForAlerts,
        alertCount: parsedAlerts.length
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
